// components/providers/StaleBuildRecovery.tsx
'use client';

import { useEffect } from 'react';

/**
 * Recover from a deploy landing under an open tab.
 *
 * Every deploy gives Next a new build id: a tab opened before it keeps asking for the OLD
 * chunk files, which the new server no longer has. The next click then dies on a failed
 * dynamic import ("ChunkLoadError", "Failed to fetch dynamically imported module", ...) and
 * the user gets the browser's "This page couldn't load" screen over and over until they think
 * of a hard refresh themselves — which is exactly what a leasing manager reported on preprod
 * (2026-07-24, a screen "toutes les 2 minutes" during a deploy window).
 *
 * This listens for that specific failure and reloads ONCE per minute at most: reload picks up
 * the new build and the app is healthy again. The cooldown lives in sessionStorage so a
 * genuinely broken deploy cannot trap the tab in a reload loop.
 */
const RELOAD_STAMP_KEY = 'fater:stale-build-reload-at';
const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_FAILURE_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [^ ]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];

const isChunkFailureMessage = (message: unknown): boolean =>
  typeof message === 'string' && CHUNK_FAILURE_PATTERNS.some((pattern) => pattern.test(message));

const reloadOnceForNewBuild = (reason: string) => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) || 0);
    if (Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable: still better to reload than to strand the user.
  }
  console.warn(`[StaleBuildRecovery] ${reason} — reloading to pick up the current build.`);
  window.location.reload();
};

export default function StaleBuildRecovery() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      if (isChunkFailureMessage(event?.message) || isChunkFailureMessage((event?.error as Error | undefined)?.message)) {
        reloadOnceForNewBuild('chunk load failed');
      }
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event?.reason as { message?: unknown; name?: unknown } | undefined;
      if (
        isChunkFailureMessage(reason?.message) ||
        (typeof reason?.name === 'string' && reason.name === 'ChunkLoadError')
      ) {
        reloadOnceForNewBuild('dynamic import rejected');
      }
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
